/**
 * Service Layer Auth OAuth Error Handling
 * 
 * Handles OAuth callback errors and error message mapping.
 */

export interface OAuthErrorState {
  setAuthError: (error: string | null) => void;
  setLastErrorCode: (code: string | null) => void;
  setEmail: (email: string | null) => void;
  setRole: (role: string | null) => void;
  setCsrfToken: (token: string | null) => void;
  setLoading: (loading: boolean) => void;
  setReady: (ready: boolean) => void;
  sanitizeUrl: () => void;
}

/**
 * The error codes that may be LOGGED (Codex P2 + P1 #2216). An OAuth error code is not always
 * ours: it is the callback's `error` parameter, i.e. whatever stood in the URL. A shape check would
 * still let an opaque token through, so a code is logged only when it is one we know — the service
 * layer's own (callback result + the redirect errors mapped below), the OAuth 2.0 error codes
 * (RFC 6749 §4.1.2.1) and the ones Supabase Auth puts on the callback. Anything else is the fixed
 * word. ONE list: pages/auth/AuthCallback imports it from here.
 */
const LOGGABLE_ERROR_CODES = new Set<string>([
  'NO_WINDOW', 'CODE_EXCHANGE_FAILED', 'MISSING_TOKENS', 'COOKIE_HANDOFF_FAILED',
  'missing_flow_id', 'cache_error', 'flow_expired', 'oauth_timeout', 'token_exchange_failed',
  'access_denied', 'invalid_request', 'unauthorized_client', 'unsupported_response_type',
  'invalid_scope', 'server_error', 'temporarily_unavailable',
  'bad_oauth_state', 'bad_oauth_callback', 'flow_state_not_found', 'flow_state_expired',
  'otp_expired', 'unexpected_failure', 'validation_failed', 'provider_disabled', 'signup_disabled',
])

export function loggableErrorCode(code: string | null | undefined): string {
  if (code === null || code === undefined || code === '') return 'UNKNOWN'
  return LOGGABLE_ERROR_CODES.has(code) ? code : 'unrecognised'
}

/**
 * Handle OAuth error from URL parameters
 */
export function handleOAuthError(
  error: string,
  urlParams: URLSearchParams,
  state: OAuthErrorState
): void {
  // Extract correlation IDs for support/debugging
  const flowId = urlParams.get('flow');
  const reqId = urlParams.get('req_id');
  const errorDescription = urlParams.get('error_description');

  // Normalized error logging with correlation IDs — and ONLY those. The full URL and the full
  // parameter set are deliberately NOT logged (TASK-FE-AUTH-CALLBACK-NO-TOKEN-LOGS-SENTRY-
  // REDACTION-2026-10-09): an OAuth return can carry `code=` or tokens in the query or the
  // fragment, and every console line is copied into Sentry breadcrumbs and bug-report bundles.
  // The four parameters that ARE logged come from the same URL (Codex P1 x2 #2216), so NO VALUE
  // of them is logged: the error only as a known code; of flow / req_id only whether each came
  // (no shape check can tell a correlation id from an opaque credential of the same alphabet);
  // of the description — free provider text — only whether it is there and how long it is.
  // Support correlates by time and by the server's own logs, which hold the real ids.
  console.error('[ServiceLayerAuth] OAuth error:', {
    error: loggableErrorCode(error),
    auth_flow_id_present: flowId !== null && flowId !== '',
    request_id_present: reqId !== null && reqId !== '',
    error_description_present: errorDescription !== null && errorDescription !== '',
    error_description_length: errorDescription === null ? 0 : errorDescription.length,
  });
  
  // Clean up URL (removes query params and trailing slashes)
  state.sanitizeUrl();
  
  // Map error codes to user-friendly messages (updated to match Service Layer error codes)
  let errorMessage = 'Authentication failed. Please try again.';
  switch (error) {
    case 'missing_flow_id':
      errorMessage = 'Login session expired. Please sign in again.';
      break;
    case 'cache_error':
      errorMessage = 'Authentication service error. Please try again.';
      break;
    case 'flow_expired':
      errorMessage = 'Login session timed out. Please try again.';
      break;
    case 'oauth_timeout':
      errorMessage = 'Connection timeout. Please check your network and try again.';
      break;
    case 'token_exchange_failed':
      errorMessage = 'Authentication failed. Please try signing in again.';
      break;
    default:
      // Unknown error - log it for debugging (a known code, or the fixed word: never the raw parameter)
      console.error('[ServiceLayerAuth] Unknown OAuth error:', loggableErrorCode(error));
      errorMessage = 'Authentication error occurred. Please try signing in again.';
  }
  
  // Store raw error code for UI tailoring
  state.setLastErrorCode(error);
  
  // Use error_description from Service Layer if available (more specific)
  if (errorDescription) {
    errorMessage = errorDescription;
  }
  
  // Set error state for UI display
  state.setAuthError(errorMessage);
  
  // Auto-clear error after 30 seconds to avoid stale banners
  setTimeout(() => {
    // Only clear if it's still the same error (user might have seen it and started new login)
    // Note: This requires access to current error state, which we don't have here
    // The caller should handle this if needed, or we can pass a getter function
    // For now, we'll just clear it after 30 seconds
    state.setAuthError(null);
  }, 30000);
  
  // Clear email state (user is not authenticated)
  state.setEmail(null);
  state.setRole(null);
  state.setCsrfToken(null); // Clear CSRF if not authenticated
  state.setLoading(false);
  state.setReady(true); // Mark as ready even on error
}

